import type { Color } from "@mui/material";
import { Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { Provider } from "react-redux";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader.tsx";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle.ts";
import { BalanceSheetIncomeBalanceType } from "../../../../../api/types/svTypes.ts";
import DrillDownEntry from "../../sv/components/builder/common/drillDown/DrillDownEntry.tsx";
import { DeveloperPanelWrapper } from "../../sv/components/builder/dev/DeveloperPanelWrapper.tsx";
import { BiCommonDataContextProvider } from "../../sv/contexts/BiCommonDataContextProvider.tsx";
import { store } from "../../sv/store/store.ts";
import { changeDataSet } from "../../sv/store/thunks/metaDataThunks.ts";
import { DataSetIds } from "../../sv/utilities/DimensionNames.ts";
import { standardReportsByKey } from "../standardReportsDefinitions.tsx";
import BalanceSheetView from "./BalanceSheetView.tsx";
import { BalanceSheetContext } from "./contexts/BalanceSheetContext.tsx";
import useBalanceSheetState from "./hooks/useBalanceSheetState.ts";
import {
  STANDARD_BALANCE_SHEET_REPORT_ID,
  STANDARD_INCOME_STATEMENT_REPORT_ID,
} from "./utils/buildBalanceSheetRequest.ts";
import type { BalanceSheetPreloadedProps } from "./withBalanceSheetPreload.tsx";
import withBalanceSheetPreload from "./withBalanceSheetPreload.tsx";

interface StandardBalanceSheetReportConfig {
  title: string;
  reportId: string;
  incomeBalanceType: BalanceSheetIncomeBalanceType;
  loadErrorTitle: string;
  icon: React.ReactNode;
  iconColor: Color;
}

const balanceSheetReportConfig: StandardBalanceSheetReportConfig = {
  title: "Balance Sheet",
  reportId: STANDARD_BALANCE_SHEET_REPORT_ID,
  incomeBalanceType: BalanceSheetIncomeBalanceType.BalanceSheet,
  loadErrorTitle: "Failed to load balance sheet",
  icon: <standardReportsByKey.balanceSheet.Icon />,
  iconColor: standardReportsByKey.balanceSheet.iconColor,
};

const incomeStatementReportConfig: StandardBalanceSheetReportConfig = {
  title: "Income Statement",
  reportId: STANDARD_INCOME_STATEMENT_REPORT_ID,
  incomeBalanceType: BalanceSheetIncomeBalanceType.IncomeStatement,
  loadErrorTitle: "Failed to load income statement",
  icon: <standardReportsByKey.incomeStatement.Icon />,
  iconColor: standardReportsByKey.incomeStatement.iconColor,
};

const createStandardBalanceSheetReport = (config: StandardBalanceSheetReportConfig) => {
  const StandardBalanceSheetReportContent = ({ settings, entityOptions }: BalanceSheetPreloadedProps) => {
    usePageTitle(config.title);

    const state = useBalanceSheetState({
      reportId: config.reportId,
      incomeBalanceType: config.incomeBalanceType,
      settings,
      entityOptions,
    });

    const { dimensions, dimensionsStructure, measures, dataSetId, books, isMultiBook, refreshToken, refresh } = state;

    return (
      <BalanceSheetContext value={state}>
        <BiCommonDataContextProvider
          books={books}
          isMultiBook={isMultiBook}
          dataSetId={dataSetId}
          dimensionsStructure={dimensionsStructure}
          dimensions={dimensions}
          editMode={false}
          conditions={[]}
          reportId={config.reportId}
          refreshToken={refreshToken}
          dimensionNames={dimensions.map((d) => d.name)}
          measureNames={measures.map((m) => m.name)}
          onConditionUpdate={() => {}}
          onRefreshGrid={refresh}
        >
          <BalanceSheetView
            reportTitle={config.title}
            reportId={config.reportId}
            reportIcon={config.icon}
            reportIconColor={config.iconColor}
          />
          <DrillDownEntry reportId={config.reportId} />
        </BiCommonDataContextProvider>
      </BalanceSheetContext>
    );
  };

  const StandardBalanceSheetPage = withBalanceSheetPreload(StandardBalanceSheetReportContent, {
    errorTitle: config.loadErrorTitle,
  });

  const StandardBalanceSheetReport = () => {
    const [isStoreInitialized, setIsStoreInitialized] = useState(false);

    useEffect(() => {
      let isActive = true;

      const initializeStore = async () => {
        await store.dispatch(changeDataSet(DataSetIds.GeneralAndMemo));
        if (isActive) {
          setIsStoreInitialized(true);
        }
      };

      void initializeStore();

      return () => {
        isActive = false;
      };
    }, []);

    if (!isStoreInitialized) {
      return <InlineLoader />;
    }

    return (
      <Provider store={store}>
        <Stack direction="row" spacing={2} sx={{ flex: 1 }}>
          <StandardBalanceSheetPage />
          <DeveloperPanelWrapper />
        </Stack>
      </Provider>
    );
  };

  return StandardBalanceSheetReport;
};

const BalanceSheetReport: React.FC = createStandardBalanceSheetReport(balanceSheetReportConfig);

const IncomeStatementReport: React.FC = createStandardBalanceSheetReport(incomeStatementReportConfig);

// eslint-disable-next-line react-refresh/only-export-components
export { IncomeStatementReport };

export default BalanceSheetReport;
